import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { MapHeader } from "./MapHeader";
import { MapFilters } from "./MapFilters";
import { MapLocation } from "./MapLocation";
import { MapContainer, Pane, useMap } from "react-leaflet";
import { Z_INDEX } from "@app/shared/styles/zIndex";
import { Marker, MarkerLayer } from 'react-leaflet-marker';
import { Stop, Tour } from "@app/shared/models/tour.type";
import { atom_tourState, atom_isDesktop, atom_routeToStopLocation, atom_fitBoundsRequest, atom_showSmartAgent, atom_isNavigationMode } from "@app/shared/state/tour.atom";
import { useRecoilValue, useRecoilState, useRecoilCallback, useSetRecoilState } from "recoil";
import { atom_activeStopState } from "@app/shared/state/active-stop.atom";
import { atom_openedStopState } from "@app/shared/state/opened-stop.atom";
import { OfflineTileLayer } from '@app/features/map/offline-tile-layer/OfflineTileLayer';
import { ListRender } from "./ListRender";
import { InitialDataFetch } from "@app/features/initial-data-fetch/InitialDataFetch";
import { LatLngExpression } from "leaflet";
// Order is critical: setup file populates window.L, then leaflet-rotate patches L.Map.
// See shared/utils/leaflet-rotate-setup.ts for the why.
import '@app/shared/utils/leaflet-rotate-setup';
import 'leaflet-rotate';
import { PolylineDecorator } from "@app/shared/components/map/PolylineDecorator";
import { TourPinV2 } from "@app/shared/components/tour-pin/TourPinV2";
import { atom_selectedFilterState, atom_bookmarkedStopsState, selector_isSavedMode } from "@app/shared/state/filter.atom";
import { DeviceLocationV2 } from "@app/features/map/device-location-layer/DeviceLocationV2";
import { atom_showModalContent, atom_modalType, atom_showClustersAndMarkers, atom_navigationHelperState, atom_smallStopImageWidth, atom_plannedRoute } from "@app/shared/state/show-content-modal.atom";
import { atom_geolocationState } from "@app/shared/state/geolocation.atom";
import { useTranslation } from "react-i18next";
import { AccessibleModal } from "@app/shared/components/accessibility/AccessibleModal";
import { announce } from "@app/shared/components/accessibility/LiveAnnouncer";
import SmartAgent from "@app/pages/smart-agent/SmartAgent";
import { atom_tourStage } from "@app/shared/state/loading.atom";
import { atom_isMapReady } from "@app/shared/state/map-ready.atom";
import { renderModalContent } from "@app/shared/components/modalComponents/ModalContentRenderer";
import NavigationHelperContent from '@app/shared/components/NavigationHelperContent';
import { useSupercluster } from '@app/shared/hooks/useSupercluster';
import './animated.css'
import { useGlobalHooks } from "@app/core/hooks/global-hooks";
import { useMapNavigation } from "@app/shared/hooks/useMapNavigation";
import polyline from '@mapbox/polyline';
import { atom_savedStopState } from "@app/shared/state/saved-stop-state.atom";
import type { SavedStopState } from "@app/shared/state/saved-stop-state.atom";
import { TourBoundaries } from './TourBoundaries';
import { passiveCollector, isWithinRadius } from '@app/shared/services/passive-data/passive-data-collector';
import { MapLocationButton } from './MapLocationButton';
import { MapDropPinControls } from './MapDropPinControls';
import { MapPinLayer } from './MapPinLayer';
import { useMapRoutes } from '@app/shared/hooks/useMapRoutes';
import { SideBar } from '@app/shared/components/sidebar-buttons/SideBar';
import { useRenderTracker, renderStore } from '@app/shared/debug';
import { atom_listModeState, atom_mobileListHeight } from '@app/shared/state/list-mode.atom';
import { selector } from 'recoil';

// Only triggers re-render when this specific boolean flips, not on every list mode change
const selector_isPlannedRouteResults = selector({
  key: 'mapContainer_isPlannedRouteResults',
  get: ({ get }) => get(atom_listModeState) === 'mobileUserPlannedTourResults',
});
import { atom_userAddedPlaces, atom_selectedUserPlace } from '@app/shared/state/user-added-stops.atom';
import { atom_dropPinMode } from '@app/shared/state/drop-pin-mode.atom';
import { atom_mapRef, atom_flyToSelectedStop, atom_flyToFitStops } from '@app/shared/state/map-ref.atom';
import { NotificationToast } from '@app/shared/components/NotificationToast';
import { DesktopStopsCount } from '@app/pages/map-page/MapOverlays';
import { usePolylineAnimation } from '@app/shared/hooks/usePolylineAnimation';
import { atom_tourNotifications } from '@app/shared/state/notification.atom';

// Component to auto-focus map when filters change
function MapFilterFocusHandler({
  stops,
  flyToFitStops,
  doneFirstZoomRef,
  selectedFilters,
  originalTour
}: {
  stops: Stop[] | undefined;
  flyToFitStops: (stops: Stop[], options?: { padding?: number; maxZoom?: number }) => void;
  doneFirstZoomRef: React.MutableRefObject<boolean>;
  selectedFilters: string[];
  originalTour: Tour | undefined;
}) {

  useEffect(() => {
    // Only zoom if:
    // 1. Initial zoom has been done
    // 2. We have stops
    // 3. Filters are actually active (not showing full tour)
    const hasActiveFilters = selectedFilters.length > 0 && !selectedFilters.includes('All');
    const isFilteredView = stops && originalTour && stops.length < originalTour.stops.length;
    if (doneFirstZoomRef.current && stops && stops.length > 1 && (hasActiveFilters || isFilteredView)) {
      // Automatically fit all visible stops when filters are applied (only if more than 1 stop)
      flyToFitStops(stops, { padding: 50, maxZoom: 18 });
    } else if (doneFirstZoomRef.current && stops && stops.length === 1 && (hasActiveFilters || isFilteredView)) {
    } else if (doneFirstZoomRef.current && !hasActiveFilters && !isFilteredView) {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stops, selectedFilters]);

  return null;
}

// Component to handle map zoom changes
function MapZoomHandler({
  onZoomChange,
  onUpdateClusters,
  filteredStops,
  stopId,
  onMapReady,
  initialFocusStop,
  hasSetInitialView,
  tourConfigFocus,
  savedStopState,
  initialFlyDuration,
  doneFirstZoomRef,
  getGeolocation,
  onUserDrag
}: {
  onZoomChange: (zoom: number) => void;
  onUpdateClusters: (zoom: number) => void;
  filteredStops: Stop[] | undefined;
  stopId: string | undefined;
  onMapReady?: () => void;
  initialFocusStop?: Stop | undefined;
  hasSetInitialView?: React.MutableRefObject<boolean> | undefined;
  tourConfigFocus?: number | undefined;
  savedStopState?: SavedStopState | null | undefined;
  initialFlyDuration?: React.MutableRefObject<number>;
  doneFirstZoomRef: React.MutableRefObject<boolean>;
  getGeolocation?: () => any;
  onUserDrag?: () => void;
}) {
  const map = useMap();
  // Default mobile list height: 25% of viewport + 100px (matches MobileListContent initialHeight)
  const DEFAULT_MOBILE_LIST_HEIGHT = Math.floor(window.innerHeight * 0.25) + 100;

  useEffect(() => {
    const handleZoomEnd = () => {
      const currentZoom = Math.round(map.getZoom());
      onZoomChange(currentZoom);
      onUpdateClusters(currentZoom); // Update clusters when zoom ends
      doneFirstZoomRef.current = true;
    }
    const handleMoveEnd = () => {
      const currentZoom = Math.round(map.getZoom());
      onUpdateClusters(currentZoom); // Update clusters when map movement ends
    };

    // Initial load handler with delay
    const handleReady = () => {
      const initialZoom = map.getZoom();
      const currentCenter = map.getCenter();

      setTimeout(() => {
        onZoomChange(initialZoom);
        onUpdateClusters(initialZoom); // Update clusters on initial load

        // One-time initial view logic. Prefer config-driven behavior:
        // - If `tourConfigFocus === 0` -> fit bounds to all stops (filteredStops)
        // - Otherwise, if an `initialFocusStop` exists -> fly to that stop
        const duration = initialFlyDuration?.current ?? 3.0;
        if (!hasSetInitialView?.current) {
          const isMobile = window.innerWidth < 1024;
          const listH = isMobile ? DEFAULT_MOBILE_LIST_HEIGHT : 0;
          try {
            if (savedStopState && initialFocusStop && savedStopState.stopId) {
              const lat = initialFocusStop.stop_location.latitude;
              const lng = initialFocusStop.stop_location.longitude;
              let targetLat = lat;
              let targetLng = lng;
              if (isMobile && listH > 0) {
                const point = map.project([lat, lng], 17);
                point.y += (listH - 105) / 2;
                const offset = map.unproject(point, 17);
                targetLat = offset.lat;
              }
              // Desktop/tablet: if BigStopPage will open, shift target west so marker lands in the visible map area
              if (!isMobile && savedStopState.modalType === 'bigStop') {
                const vw = window.innerWidth;
                const panelWidth = Math.max(Math.floor(vw >= 1400 ? vw * 0.25 : vw >= 1100 ? vw * 0.33 : vw >= 1000 ? vw * 0.4 : (vw - 85) / 2), 400);
                const point = map.project([targetLat, targetLng], 17);
                point.x -= panelWidth / 2;
                const offset = map.unproject(point, 17);
                targetLat = offset.lat;
                targetLng = offset.lng;
              }
              map.flyTo([targetLat, targetLng], 17, { animate: true, duration });
              if (hasSetInitialView) hasSetInitialView.current = true;
            } else if (typeof tourConfigFocus !== 'undefined' && Number(tourConfigFocus) === 0) {
              if (filteredStops && filteredStops.length > 0) {
                const latLngs = filteredStops
                  .filter(s => s?.stop_location)
                  .map(s => [s.stop_location.latitude, s.stop_location.longitude]) as [number, number][];
                if (latLngs.length > 0) {
                  const sidePad = isMobile ? 20 : 50;
                  const topPad = isMobile ? 105 + 20 : 50;
                  const bottomPad = isMobile ? listH : sidePad;
                  try {
                    (map as any).flyToBounds(latLngs, {
                      paddingTopLeft: [sidePad, topPad],
                      paddingBottomRight: [sidePad, bottomPad],
                      maxZoom: 16,
                      duration,
                    });
                    if (hasSetInitialView) hasSetInitialView.current = true;
                  } catch (err) {
                    const sum = latLngs.reduce((acc, cur) => [acc[0] + cur[0], acc[1] + cur[1]] as [number, number], [0, 0]);
                    const center: [number, number] = [sum[0] / latLngs.length, sum[1] / latLngs.length];
                    map.flyTo(center, 16, { animate: true, duration });
                    if (hasSetInitialView) hasSetInitialView.current = true;
                  }
                }
              }
            } else if (initialFocusStop?.stop_location) {
              const lat2 = initialFocusStop.stop_location.latitude;
              const lng2 = initialFocusStop.stop_location.longitude;
              let targetLat2 = lat2;
              if (isMobile && listH > 0) {
                const point = map.project([lat2, lng2], 16);
                point.y += (listH - 105) / 2;
                const offset = map.unproject(point, 16);
                targetLat2 = offset.lat;
              }
              map.flyTo([targetLat2, lng2], 16, { animate: true, duration });
              if (hasSetInitialView) hasSetInitialView.current = true;
            }
          } catch (err) {
            // ignore non-fatal errors during initial positioning
          }
        }

        // Notify parent that initial zoom/cluster setup completed
        onMapReady && onMapReady();
      }, 0);
    };
    // Handle user drag — disable tracking
    const handleDragStart = () => {
      onUserDrag && onUserDrag();
    };
    // Mobile-optimized events
    map.on('zoomend', handleZoomEnd);     // After pinch-to-zoom ends
    map.on('moveend', handleMoveEnd);     // After panning ends
    map.on('ready', handleReady);         // Initial map ready
    map.on('dragstart', handleDragStart); // When user starts dragging

    // Call the ready handler immediately in case the Leaflet instance doesn't emit a 'ready' event
    // This ensures initial zoom/cluster setup runs and parent onMapReady is called.
    // ONLY run on first initialization - don't re-run when filters change
    if (!hasSetInitialView?.current) {
      setTimeout(() => {
        try {
          handleReady();
        } catch (err) {
          // swallow - non-fatal
        }
      }, 0);
    }

    return () => {
      map.off('zoomend', handleZoomEnd);
      map.off('moveend', handleMoveEnd);
      map.off('ready', handleReady);
      map.off('dragstart', handleDragStart);
    };
  }, [map, onZoomChange, filteredStops, stopId, onUserDrag]);

  return null;
}



// Simple zoom controls (+ / -) overlaying the map
function ZoomControls({ onZoomChange, isDesktop, onLocationClick }: { onZoomChange?: (zoom: number) => void; isDesktop?: boolean; onLocationClick?: () => void }) {
  const map = useMap();
  const { t, i18n } = useTranslation();
  const isRtl = (i18n as any).language === 'he';

  function handleZoomIn() {
    map.zoomIn();
    onZoomChange && onZoomChange(map.getZoom());
  }

  function handleZoomOut() {
    map.zoomOut();
    onZoomChange && onZoomChange(map.getZoom());
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        right: isDesktop ? 22 : (isRtl ? 'auto' : 22),
        left: isDesktop ? 'auto' : (isRtl ? 22 : 'auto'),
        bottom: 90,
        zIndex: Z_INDEX.mapButton,
        bgcolor: 'white',
        borderRadius: '16px',
        boxShadow: '0px 0px 6px 0px rgba(0,37,74,0.10)',
        border: '1px solid',
        borderColor: 'rgb(236, 236, 236)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Location button on top */}
      <Box sx={{ position: 'relative', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <MapLocation onClick={onLocationClick} />
        </Box>
      </Box>
      <Box sx={{ height: 1, bgcolor: 'rgba(0,0,0,0.08)' }} />
      <IconButton aria-label={t('accessibility.zoom_in') as string} onClick={handleZoomIn} sx={{ width: 44, height: 44 }}>
        <i className="fa-solid fa-plus" aria-hidden="true"></i>
      </IconButton>
      <Box sx={{ height: 1, bgcolor: 'rgba(0,0,0,0.08)' }} />
      <IconButton aria-label={t('accessibility.zoom_out') as string} onClick={handleZoomOut} sx={{ width: 44, height: 44 }}>
        <i className="fa-solid fa-minus" aria-hidden="true"></i>
      </IconButton>
    </Box>
  );
}

// Isolated: reacts to fitBoundsRequest without re-rendering the map
const FitBoundsHandler = React.memo(function FitBoundsHandler({
  mapRef,
}: {
  mapRef: React.MutableRefObject<any>;
}) {
  const [fitBoundsRequest, setFitBoundsRequest] = useRecoilState(atom_fitBoundsRequest);
  const isDesktop = useRecoilValue(atom_isDesktop);

  useEffect(() => {
    if (!fitBoundsRequest || !mapRef.current) return;
    const sidePadding = isDesktop ? 100 : 50;
    const topPadding = isDesktop ? sidePadding : 105 + sidePadding;
    // bottomPadding is the live panel-height the caller measured AFTER its
    // destination panel mounted (see flyAfterLayout). No fudge factor.
    const bottomPadding = fitBoundsRequest.bottomPadding ?? sidePadding;

    mapRef.current.fitBounds(fitBoundsRequest.bounds, {
      animate: true,
      paddingTopLeft: [sidePadding, topPadding] as [number, number],
      paddingBottomRight: [sidePadding, bottomPadding] as [number, number],
      maxZoom: 17,
    });
    setFitBoundsRequest(null);
  }, [fitBoundsRequest, isDesktop, setFitBoundsRequest, mapRef]);

  return null;
});

// Subscribes to atom_isNavigationMode in isolation so the navigation toggle
// doesn't re-render the heavy AppMapContainer. Renders children only when active.
const NavigationModeGate = React.memo(function NavigationModeGate({ children }: { children: React.ReactNode }) {
  const isNavigationMode = useRecoilValue(atom_isNavigationMode);
  return isNavigationMode ? <>{children}</> : null;
});

// Hides the tour's main polylines while the user is actively navigating to a specific stop,
// so the map shows only the route-to-stop polyline (less clutter). Subscribes alone so
// AppMapContainer stays unsubscribed from these toggles.
const TourRouteGate = React.memo(function TourRouteGate({ children }: { children: React.ReactNode }) {
  const isNavigationMode = useRecoilValue(atom_isNavigationMode);
  const routeToStop = useRecoilValue(atom_routeToStopLocation);
  const hasActiveStopNavigation = !!routeToStop && isNavigationMode;
  return hasActiveStopNavigation ? null : <>{children}</>;
});

// Mirrors the map's current bearing into a CSS custom property on the map container.
// Marker content tagged with `counter-rotate` reads it via CSS and stays upright while the map rotates.
// Pure DOM mutation — no React state, no re-renders for the consumer.
const MapBearingCSSSync = React.memo(function MapBearingCSSSync() {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const apply = () => {
      const bearing = (map as any).getBearing ? (map as any).getBearing() : 0;
      map.getContainer().style.setProperty('--map-bearing', String(-bearing));
    };
    map.on('rotate', apply);
    apply();
    return () => { map.off('rotate', apply); };
  }, [map]);
  return null;
});

// Isolated: subscribes to tourStage so AppMapContainer doesn't have to
const MapStageControls = React.memo(function MapStageControls({
  isDesktop,
  sidebarWidthPx,
  onZoomChange,
  onLocationClick,
  containerRef,
  mapRef,
  isTrackingRef,
  locationBtnStateRef,
  getGeolocation,
  allStops,
  flyToFitStops,
}: {
  isDesktop: boolean;
  sidebarWidthPx: number;
  onZoomChange: (zoom: number) => void;
  onLocationClick: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
  mapRef: React.MutableRefObject<any>;
  isTrackingRef: React.MutableRefObject<boolean>;
  locationBtnStateRef: React.MutableRefObject<'user' | 'stops'>;
  getGeolocation: () => { latitude: number | null; longitude: number | null };
  allStops: any[];
  flyToFitStops: (stops: any[], opts?: any) => void;
}) {
  const tourStage = useRecoilValue(atom_tourStage);
  const tourConfig = useRecoilValue(atom_tourState)?.config;
  // SideBar icons strip (85px) is shown unless explicitly disabled via config.sidebar = 'false'
  const sidebarIconsWidth = String(tourConfig?.sidebar) !== 'false' ? 85 : 0;
  const totalSidebarWidth = sidebarWidthPx + sidebarIconsWidth;

  useEffect(() => {
    if (containerRef.current) {
      // Side-by-side on desktop: map starts where the sidebar ends (no overlap)
      containerRef.current.style.left = isDesktop && tourStage === 'map'
        ? `${totalSidebarWidth}px`
        : '0px';
      // Notify Leaflet the container resized so tiles re-render correctly
      try {
        (mapRef.current as any)?.invalidateSize?.();
      } catch { /* ignore */ }
    }
  }, [tourStage, isDesktop, totalSidebarWidth, containerRef]);

  if (tourStage !== 'map') return null;

  return (
    <>
      {isDesktop && (
        <ZoomControls
          onZoomChange={onZoomChange}
          isDesktop={isDesktop}
          onLocationClick={onLocationClick}
        />
      )}
      <MapLocationButton
        mapRef={mapRef}
        isTrackingRef={isTrackingRef}
        locationBtnStateRef={locationBtnStateRef}
        getGeolocation={getGeolocation}
        allStops={allStops}
        flyToFitStops={flyToFitStops}
      />
      {isDesktop && <DesktopStopsCount />}
    </>
  );
});

export const AppMapContainer = React.memo(function AppMapContainer() {
  useRenderTracker('NewMapContainer');
  const [currentHeight, setCurrentHeight] = useState<number>(100);
  const [position, setPosition] = useState<LatLngExpression>([0, 0]);
  const selectedFilter = useRecoilValue(atom_selectedFilterState);
  // openedStopState moved to ModalOverlay — no subscription needed here
  const [state_activeStopState, setState_activeStopState] = useRecoilState(atom_activeStopState);
  const setState_showModal = useSetRecoilState(atom_showModalContent);
  const setState_modalType = useSetRecoilState(atom_modalType);
  const { t, i18n } = useTranslation();
  // SmartAgent moved to MapOverlays — no subscription needed here
  const [currentZoom, setCurrentZoom] = useState(13);
  const [isGroupPinClicked, setIsGroupPinClicked] = useState(false);
  const setIsMapReady = useSetRecoilState(atom_isMapReady);
  const isDesktop = useRecoilValue(atom_isDesktop);
  const setListMode = useSetRecoilState(atom_listModeState);
  // mobileListHeight read on-demand — only used in handleClickOnPin
  const [sidebarWidthPx, setSidebarWidthPx] = useState<number>(0);
  const mapRef = useRef<any>(null);
  const clickHandlersMap = useRef(new WeakMap<Element, () => void>());
  const setMapRefAtom = useSetRecoilState(atom_mapRef);
  const setSelectedUserPlace = useSetRecoilState(atom_selectedUserPlace);
  const doneFirstZoomRef = useRef(false);
  const hasSetInitialViewRef = useRef(false);
  const mapContainerDivRef = useRef<HTMLDivElement>(null);
  const _g = useGlobalHooks();
  // tourNotifications + mobileListHeight + savedStopState read on-demand — not needed for rendering
  const getOnDemand = useRecoilCallback(({ snapshot }) => () => ({
    geolocation: snapshot.getLoadable(atom_geolocationState).getValue(),
    tourNotifications: snapshot.getLoadable(atom_tourNotifications).getValue(),
    mobileListHeight: snapshot.getLoadable(atom_mobileListHeight).getValue(),
    savedStopState: snapshot.getLoadable(atom_savedStopState).getValue(),
  }), []);

  const setOpenedStop = useRecoilCallback(({ set }) => (stop: Stop) => {
    set(atom_openedStopState, stop);
  }, []);

  const state_TourState = useRecoilValue(atom_tourState);

  const [userAddedPlaces, setUserAddedPlaces] = useRecoilState(atom_userAddedPlaces);

  const [dropPinMode, setDropPinMode] = useRecoilState(atom_dropPinMode);

  // Continuous tracking — disabled on drag to stop DeviceLocationV2 setView
  const isTrackingRef = useRef(false);
  // Button toggle — only changes on button click, not on drag
  const locationBtnStateRef = useRef<'user' | 'stops'>('stops');

  const allStops = useMemo(() => {
    return state_TourState?.stops || [];
  }, [state_TourState?.stops]);


  const [state_routeToStopLocation, setState_RouteToStopLocation] = useRecoilState(atom_routeToStopLocation);
  const { guideRouteAnimated, routeToStopAnimated, activeGuideSegment } = usePolylineAnimation();
  const state_showClustersAndMarkers = useRecoilValue(atom_showClustersAndMarkers);
  const plannedRoute = useRecoilValue(atom_plannedRoute);
  const isPlannedRouteResults = useRecoilValue(selector_isPlannedRouteResults);

  // Visible stops: computed locally using boolean selector (only flips on saved toggle, not every sidebar click)
  const bookmarkedStops = useRecoilValue(atom_bookmarkedStopsState);
  const isSavedMode = useRecoilValue(selector_isSavedMode);

  const visibleStops = useMemo(() => {
    let stops = allStops;

    if (isSavedMode && bookmarkedStops.length > 0) {
      stops = allStops.filter(s => bookmarkedStops.includes(s.id));
    }

    if (selectedFilter.length > 0) {
      stops = stops.filter(s =>
        s.tags?.some((t: any) => selectedFilter.includes(t.text))
      );
    }

    if (isPlannedRouteResults && plannedRoute?.stopOrder?.length) {
      stops = stops.filter(s => plannedRoute.stopOrder.includes(s.id));
    }

    return stops;
  }, [allStops, bookmarkedStops, isSavedMode, selectedFilter, isPlannedRouteResults, plannedRoute?.stopOrder]);

  const initialFlyDuration = useRef<number>(3.0);

  const { uID, tID, gID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
  const isDemo = useMemo(() => {
    return uID?.toLowerCase() === 'demo';
  }, [uID, tID, gID]);

  const demoLimit = typeof state_TourState?.config?.demo_stop === 'number' ? (state_TourState!.config.demo_stop as number) : 2;

  const {
    clusters, individualMarkers, clusteredUserPlaceIds,
    animatedMarkerIds, animatedClusterIds,
    supercluster, updateClusters,
  } = useSupercluster({
    mapRef,
    stops: visibleStops,
    userAddedPlaces: userAddedPlaces[state_TourState?.id || ''] || [],
    tourId: state_TourState?.id || '',
    isDemo,
    demoLimit,
  });

  const { flyToSelectedStop, flyToFitStops, handleClusterClick } = useMapNavigation({
    mapRef,
    supercluster,
    stops: visibleStops,
    activeStopId: state_activeStopState?.id,
    isDesktop,
  });

  // Expose flyToSelectedStop globally so ListRender (sibling) can use it
  // const setFlyToSelectedStop = useSetRecoilState(atom_flyToSelectedStop);
  // useEffect(() => {
  //   setFlyToSelectedStop(() => flyToSelectedStop);
  // }, [flyToSelectedStop, setFlyToSelectedStop]);

  // Expose flyToFitStops globally so MapFilters/SavedButton can delegate saved-bounds fly here
  const setFlyToFitStops = useSetRecoilState(atom_flyToFitStops);
  useEffect(() => {
    setFlyToFitStops(() => flyToFitStops);
  }, [flyToFitStops, setFlyToFitStops]);

  const minHeight = 100;
  const maxHeight = typeof window !== 'undefined' ? window.innerHeight - 60 : 800;
  const snapPoints = [minHeight, 339, maxHeight];

  useEffect(() => {
    if (!state_activeStopState?.id) {
      return;
    }
    const timeoutId = setTimeout(() => {
      flyToSelectedStop(state_activeStopState.id);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [state_activeStopState?.id]);

  // Refs for values that change frequently — avoids useCallback dep churn
  const currentZoomRef = useRef(currentZoom);
  currentZoomRef.current = currentZoom;
  const activeStopRef = useRef(state_activeStopState);
  activeStopRef.current = state_activeStopState;
  const allStopsRef = useRef(allStops);
  allStopsRef.current = allStops;

  const handleClickOnPin = useCallback((stopId: string, locked?: boolean) => {
    const clickedStop = allStopsRef.current.find(
      (stop) => stop.id === stopId
    );

    // Track marker click
    if (clickedStop) {
      passiveCollector.trackMarkerClick(clickedStop);
    }

    setTimeout(() => {
      if (
        clickedStop?.type === 'business' ||
        clickedStop?.type === 'bigStop' ||
        clickedStop?.type === 'smallStop' ||
        clickedStop?.type === 'WCStop' ||
        clickedStop?.type === 'waterStop' ||
        clickedStop?.type === 'noEntrance' ||
        clickedStop?.type === 'restaurant' ||
        clickedStop?.type === 'coffee' ||
        clickedStop?.type === 'shoppingCenter' ||
        clickedStop?.type === 'hospital' ||
        clickedStop?.type === 'streetFood' ||
        clickedStop?.type === 'localBusiness' ||
        clickedStop?.type === 'userAdded'
      ) {
        const mapCenter = mapRef.current.getCenter();
        const stopLat = clickedStop.stop_location.latitude;
        const stopLng = clickedStop.stop_location.longitude;
        const precision = Math.pow(10, -(currentZoomRef.current - 15));
        let expectedCenterLat = stopLat;
        const _mobileListHeight = getOnDemand().mobileListHeight;
        if (!isDesktop && _mobileListHeight > 0 && mapRef.current) {
          const topBarHeight = 105;
          const offsetPixels = (_mobileListHeight - topBarHeight) / 2;
          const point = mapRef.current.project([stopLat, stopLng], currentZoomRef.current);
          point.y += offsetPixels;
          const offsetLatLng = mapRef.current.unproject(point, currentZoom);
          expectedCenterLat = offsetLatLng.lat;
        }
        if (
          (Math.abs(mapCenter.lat - expectedCenterLat) < 0.003 * precision &&
            Math.abs(mapCenter.lng - stopLng) < 0.003 * precision) &&
          currentZoomRef.current >= 15 &&
          clickedStop.id === activeStopRef.current?.id &&
          !locked
        ) {
          // If StopsCardsContent would render with no direction text or no image,
          // skip it and open BigStopPage directly.
          const isSmallStop = clickedStop.type?.toLowerCase() === 'smallstop';
          const hasDirectionText = !!clickedStop.diraction_text;
          const hasLocPic = !!clickedStop.loc_pics?.[0]?.url;
          const skipStopsCards = !isSmallStop && (!hasDirectionText || !hasLocPic);

          if (skipStopsCards) {
            const geo = getOnDemand().geolocation;
            passiveCollector.trackStopViewed(clickedStop, isWithinRadius(geo.latitude, geo.longitude, clickedStop));
            setOpenedStop(clickedStop);
            announce(t('accessibility.opening_stop_details', { title: clickedStop.s_title }), 'polite');
            return;
          }

          setState_showModal(true);
          setState_modalType('stopsCards');
          // Save stop modal state to localStorage for persistence
          const stopModalStateKey = `stop_modal_state_${state_TourState?.id}`;
          localStorage.setItem(stopModalStateKey, JSON.stringify({
            stopId: activeStopRef.current?.id,
            modalType: 'stopsCards',
            timestamp: new Date().toISOString()
          }));
          announce(t('accessibility.opening_stop_details', { title: clickedStop.s_title }), 'polite');
        } else if (activeStopRef.current?.id === stopId) {
          flyToSelectedStop(stopId);
        } else {
          setState_activeStopState(clickedStop);
        }
      }
    }, 0);
  }, [isDesktop, flyToSelectedStop, setState_activeStopState, setState_showModal, setState_modalType, state_TourState?.id, t, getOnDemand, setOpenedStop]);

  const handleClickOnUserPlace = useCallback((ctx: { placeId: string; isDesktop: boolean; isMobile: boolean; classes: string[] }) => {
    if (!ctx.placeId) return;

    const tourId = state_TourState?.id || '';
    const placesForTour = (userAddedPlaces as any)[tourId] || [];
    const place = placesForTour.find((p: any) => p.place_id === ctx.placeId);

    if (!place) return;
    setSelectedUserPlace(place);
    if (mapRef.current) {
      mapRef.current.flyTo([place.geometry.location.lat, place.geometry.location.lng], 17, { duration: 1 });
    }
    if (ctx.isDesktop) {
      // Always route to dropAPin form (unified flow)
      setDropPinMode({
        isActive: false,
        step: 'naming',
        pinLocation: { lat: place.geometry.location.lat, lng: place.geometry.location.lng },
      });
      setState_modalType('dropAPin');
      setState_showModal(true);
    } else if (ctx.isMobile) {
      setListMode('mobileAddPlace');
    }
  }, [state_TourState?.id, userAddedPlaces, setSelectedUserPlace, setDropPinMode, setState_showModal, setState_modalType, setListMode]);

  // Keep refs current for the delegated handler
  const clustersRef = useRef(clusters);
  clustersRef.current = clusters;
  const handleClusterClickRef = useRef(handleClusterClick);
  handleClusterClickRef.current = handleClusterClick;
  const handleClickOnPinRef = useRef(handleClickOnPin);
  handleClickOnPinRef.current = handleClickOnPin;
  const handleClickOnUserPlaceRef = useRef(handleClickOnUserPlace);
  handleClickOnUserPlaceRef.current = handleClickOnUserPlace;

  // Single delegated click handler on the map container - attached once, never re-attached
  useEffect(() => {
    const container = document.querySelector('[data-map-container]');
    if (!container) return;

    const handler = (e: Event) => {
      const target = e.target as HTMLElement;

      // Check for tour pin click
      const pin = target.closest('.tour-pin, .tour-pin-demo');
      if (pin) {
        const classList = Array.from(pin.classList || []);
        const stopClass = classList.find((c: string) => c.startsWith('stop-id-'));
        const pinId = stopClass ? stopClass.replace('stop-id-', '') : '';
        const isLockedPin = pin.classList.contains('tour-pin-demo');

        if (pinId.startsWith('group-')) {
          setCurrentZoom(18);
          if (mapRef.current) {
            const marker = pin.closest('.leaflet-marker-icon');
            if (marker) {
              const lat = parseFloat(marker.getAttribute('data-lat') || '0');
              const lng = parseFloat(marker.getAttribute('data-lng') || '0');
              mapRef.current.setView([lat, lng], 18);
            }
          }
          return;
        }
        if (pinId) {
          handleClickOnPinRef.current(pinId, isLockedPin);
        }
        return;
      }

      // Check for cluster click
      const clusterEl = target.closest('.tour-cluster');
      if (clusterEl) {
        const clusterId = clusterEl.getAttribute('data-cluster-id');
        const cluster = clustersRef.current.find((c: any) => c.id == clusterId);
        if (cluster) {
          handleClusterClickRef.current(cluster);
        }
        return;
      }

      // Check for user-added pin click
      const userPin = target.closest('.user-added-pin');
      if (userPin) {
        e.preventDefault();
        e.stopPropagation();
        const classList = Array.from(userPin.classList || []);
        const placeClass = classList.find((c: string) => c.startsWith('place_id-'));
        const placeId = placeClass ? placeClass.slice('place_id-'.length) : '';
        if (placeId) {
          handleClickOnUserPlaceRef.current({
            placeId,
            isDesktop,
            isMobile: !isDesktop,
            classes: classList as string[],
          });
        }
        return;
      }
    };

    container.addEventListener('click', handler, true);
    return () => container.removeEventListener('click', handler, true);
  }, [isDesktop]);

  const getPinDrawer = useCallback((stop: Stop, groupIndex: number) => {
    if (isDemo && groupIndex > 1) {
      return <i className="fa-solid fa-lock scale-120" aria-hidden="true"></i>;
    }
    if (isGroupPinClicked) return '';
    if (stop.icon) {
      return <i className={`fa-solid fa-${stop.icon} scale-120`} aria-hidden="true"></i>;
    }
    return stop.stop_index?.toString() || '';
  }, [isDemo, isGroupPinClicked]);

  useEffect(() => {
    const handleResizeMode = () => {
      const width = typeof window !== 'undefined' ? window.innerWidth : 0;
      const sidebarWidth = Math.max(Math.floor(width >= 1400 ? width * 0.25 : width >= 1100 ? width * 0.33 : width >= 1000 ? width * 0.4 : (width - 85) / 2), 400);
      setSidebarWidthPx(sidebarWidth);
    };
    handleResizeMode();
    window.addEventListener('resize', handleResizeMode);
    return () => window.removeEventListener('resize', handleResizeMode);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const newMaxHeight = window.innerHeight - 60;

      if (currentHeight >= maxHeight - 10) {
        setCurrentHeight(newMaxHeight);
      }

      snapPoints[2] = newMaxHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentHeight, maxHeight]);

  const { allRoutes, routeGroupStopIds } = useMapRoutes(state_TourState, state_TourState, selectedFilter);

  useEffect(() => {
    // Re-run clustering logic to restore markers when sidebar state, tour stops, or user-added places change
    const tourId = state_TourState?.id || '';
    updateClusters(currentZoom);
  }, [state_showClustersAndMarkers, state_TourState?.stops, updateClusters, userAddedPlaces]);

  useEffect(() => {
    if (
      state_routeToStopLocation &&
      state_routeToStopLocation.bounds &&
      mapRef.current
    ) {
      const googleBounds = state_routeToStopLocation.bounds;
      const leafletBounds = [
        [googleBounds.southwest.lat, googleBounds.southwest.lng],
        [googleBounds.northeast.lat, googleBounds.northeast.lng]
      ];
      mapRef.current.fitBounds(leafletBounds, {
        animate: true,
        padding: [isDesktop ? 200 : 100, isDesktop ? 200 : 100],
      });
    }
  }, [state_routeToStopLocation]);


  const renderTransitMarkers = useMemo(() => {
    const steps = state_routeToStopLocation?.legs?.[0]?.steps;
    if (!Array.isArray(steps) || steps.length === 0) return null;

    const travelMode = (state_routeToStopLocation as any)?.travelMode as string | undefined;
    const isTransitMode = !travelMode || travelMode === 'transit';

    // Get mode icon for non-transit modes
    const getModeIcon = () => {
      switch (travelMode) {
        case 'walking': return 'fa-person-walking';
        case 'bicycling': return 'fa-bicycle';
        case 'driving': return 'fa-car';
        default: return 'fa-location-dot';
      }
    };

    // Get mode color for non-transit modes
    const getModeColor = () => {
      switch (travelMode) {
        case 'walking': return '#1976D2';
        case 'bicycling': return '#F57C00';
        case 'driving': return '#C2185B';
        default: return '#388E3C';
      }
    };

    const allCoords: [number, number][] = [];
    steps.forEach((step: any) => {
      if (step.polyline?.points) {
        const coords = polyline.decode(step.polyline.points); // [lat, lng]
        allCoords.push(...coords);
      }
    });

    const latLngs = allCoords.map(([lat, lng]) => [lat, lng]);

    // Get start location for non-transit mode marker
    const startLocation = state_routeToStopLocation?.legs?.[0]?.start_location;

    const basePinSize =
      currentZoom >= 17
        ? 32
        : currentZoom <= 14
          ? 24
          : Math.round(24 + ((currentZoom - 14) / (17 - 14)) * (32 - 24));
    const pinSize = isDesktop ? basePinSize + 7 : basePinSize;

    return (
      <React.Fragment>
        <Pane key={`transit-path-${steps.length}`} name="transit-path" style={{ zIndex: 210 }}>
          {steps.map((step: any, i: number) => {
            if (!step.polyline?.points) return null;
            const isWalkingStep = step.travel_mode === 'WALKING' || (step.html_instructions || '').toLowerCase().includes('walk');
            // For non-transit modes, don't skip any segments
            if (isTransitMode && isWalkingStep && step.distance?.value < 250) return null;
            const coords = polyline.decode(step.polyline.points);
            const latLngs = coords.map(([lat, lng]) => [lat, lng]) as LatLngExpression[];

            const innerColor = isWalkingStep ? "#F3D1B5" : "#E38854";
            const outerColor = isWalkingStep ? "#E38854" : "#F3D1B5";

            return (
              <PolylineDecorator
                key={`transit-step-polyline-${i}`}
                positions={latLngs}
                pathColor={innerColor}
                outerColor={outerColor}
                mode={isTransitMode ? (isWalkingStep ? 'transit-walking' : 'transit-vehicle') : (travelMode === 'walking' ? 'transit-walking' : 'transit-vehicle')}
                animated={routeToStopAnimated}
              />
            );
          })}
        </Pane>
        <Pane key={`transit-markers-${steps.length}`} name="transit-markers" style={{ zIndex: 211 }}>
          {isTransitMode ? (() => {
            // Build a per-step "mode key" — walking / vehicle-type+line — then only render
            // a marker when the key changes from the previous step (i.e. at mode/line transitions).
            // First step always renders. Walk→walk→walk or bus-line-A→bus-line-A collapses to one marker.
            const getModeKey = (step: any): string => {
              const instr = (step.html_instructions || '').toLowerCase().trim();
              const tm = step.travel_mode;
              if (tm === 'WALKING' || instr.includes('walk')) return 'WALK';
              if (tm === 'TRANSIT') {
                const vt = step.transit_details?.line?.vehicle?.type || '';
                const ln = step.transit_details?.line?.short_name || step.transit_details?.line?.name || '';
                return `TRANSIT:${vt}:${ln}`;
              }
              return tm || 'UNKNOWN';
            };

            return steps.map((step: any, i: number) => {
              const currKey = getModeKey(step);
              const prevKey = i > 0 ? getModeKey(steps[i - 1]) : null;
              if (prevKey !== null && currKey === prevKey) return null;

              const iconClass = (() => {
                const instr = (step.html_instructions || '').toLowerCase().trim();
                if (step.travel_mode === 'WALKING' || instr.includes('walk')) return 'fa-person-walking';
                if (step.travel_mode === 'TRANSIT' && step.transit_details?.line?.vehicle?.type === 'BUS' || instr.includes('bus')) return 'fa-bus';
                if (step.travel_mode === 'TRANSIT' && step.transit_details?.line?.vehicle?.type === 'HEAVY_RAIL' || instr.includes('train')) return 'fa-train';
                if (step.travel_mode === 'TRANSIT' && step.transit_details?.line?.vehicle?.type === 'SUBWAY' || instr.includes('subway')) return 'fa-subway';
                return 'fa-flag';
              })();

              return (
                <Marker
                  key={`transit-step-${i}`}
                  position={[step.start_location.lat, step.start_location.lng]}
                  placement="center"
                  size={[0, 0]}
                >
                  <div className="counter-rotate" style={{
                    zIndex: 1000,
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <TourPinV2
                      visited={false}
                      active={true}
                      disabled={false}
                      pinStrokeColor={state_TourState?.config?.pinColor || _g.theme.palette.primary.main}
                      pinClassName={'transit-pin'}
                      size={pinSize}
                      id={`transit-${i}`}
                    >
                      <i className={`fa-solid ${iconClass}`} />
                    </TourPinV2>
                  </div>
                </Marker>
              );
            });
          })() : (
            // Non-transit mode: show only one marker at start location
            startLocation && (
              <Marker
                key="mode-start-marker"
                position={[startLocation.lat, startLocation.lng]}
                placement="center"
                size={[0, 0]}
              >
                <div style={{
                  zIndex: 1000,
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <TourPinV2
                    visited={false}
                    active={true}
                    disabled={false}
                    pinStrokeColor={getModeColor()}
                    pinClassName={'transit-pin'}
                    size={pinSize}
                    id="mode-start"
                  >
                    <i className={`fa-solid ${getModeIcon()}`} style={{ color: getModeColor() }} />
                  </TourPinV2>
                </div>
              </Marker>
            )
          )}
        </Pane>
      </React.Fragment>
    );
  }, [state_routeToStopLocation, state_TourState, currentZoom]);

  const renderedRoutes = useMemo(() => {
    const pathColor = state_TourState?.config?.['pathColor'] || "#000";

    // When activeGuideSegment is set, split route groups into per-stop sub-segments
    // and only animate the segment leading to the target stop
    if (activeGuideSegment) {
      const elements: React.ReactNode[] = [];

      allRoutes.forEach((route, groupIdx) => {
        if (!route) return;
        const coords = route.features[0].geometry.coordinates as [number, number][];
        const wayPoints = route.features?.[0]?.properties?.way_points as number[] | undefined;
        const stopIds = routeGroupStopIds[groupIdx];

        // If no way_points or stop mapping, render whole group as static
        if (!wayPoints || !stopIds || wayPoints.length < 2) {
          const positions = coords.map((w) => [w[1], w[0]] as [number, number]);
          elements.push(
            <Pane key={`group-${groupIdx}`} name={`tour-route_${groupIdx}`} style={{ zIndex: 210 }}>
              <PolylineDecorator positions={positions} pathColor={pathColor} animated={false} />
            </Pane>
          );
          return;
        }

        // Split into sub-segments using way_points
        for (let s = 0; s < wayPoints.length - 1; s++) {
          if (s >= stopIds.length - 1) break;
          const fromId = stopIds[s];
          const toId = stopIds[s + 1];
          const segCoords = coords.slice(wayPoints[s], wayPoints[s + 1] + 1);
          const positions = segCoords.map((w) => [w[1], w[0]] as [number, number]);
          const isActive = fromId === activeGuideSegment.fromStopId && toId === activeGuideSegment.toStopId;

          elements.push(
            <Pane
              key={`seg-${groupIdx}-${s}`}
              name={`tour-route_${groupIdx}_${s}`}
              style={{ zIndex: isActive ? 230 : 210 }}
            >
              <PolylineDecorator positions={positions} pathColor={pathColor} animated={isActive} />
            </Pane>
          );
        }
      });

      return elements;
    }

    // Default: render each route group as a single polyline
    return allRoutes.filter(x => !!x).map((route, i) => {
      const positions = (route.features[0].geometry.coordinates as [number, number][]).map(
        (w) => [w[1], w[0]] as [number, number]
      );

      return (
        <Pane key={i} name={`tour-route_${i}`} style={{ zIndex: 210 }}>
          <PolylineDecorator
            positions={positions}
            pathColor={pathColor}
            animated={guideRouteAnimated}
          />
        </Pane>
      );
    });
  }, [allRoutes, state_TourState?.config, guideRouteAnimated, activeGuideSegment, routeGroupStopIds]);

  // Helper to compute pin size based on zoom and desktop state (shared between useMemos)
  const getPinSize = useCallback((zoom: number) => {
    if (isDesktop) {
      // Desktop: min 42 (<=14), max 50 (>=17), interpolate between
      return zoom >= 17
        ? 52
        : zoom <= 14
          ? 44
          : 44 + ((zoom - 14) / (17 - 14)) * (52 - 44);
    }
    // Mobile / non-desktop existing behavior
    return zoom >= 17
      ? 40
      : zoom <= 14
        ? 32
        : 32 + ((zoom - 14) / (17 - 14)) * (40 - 32);
  }, [isDesktop]);

  // Planned route polylines (rendered when user has planned a route)
  const renderPlannedRoutePolylines = useMemo(() => {
    if (!isPlannedRouteResults || !plannedRoute?.stopOrder?.length) return null;
    const stops = state_TourState?.stops || [];
    const orderedStops = plannedRoute.stopOrder
      .map(id => stops.find(s => s.id === id))
      .filter((s): s is Stop => !!s);
    if (orderedStops.length < 2) return null;

    const pathColor = state_TourState?.config?.['pathColor'] || '#E8A87C';

    // Try overview polyline from cloud function
    if (plannedRoute.overviewPolyline) {
      try {
        const decoded = polyline.decode(plannedRoute.overviewPolyline);
        const positions = decoded.map(([lat, lng]: [number, number]) => [lat, lng] as [number, number]);
        return (
          <Pane name="planned-route" style={{ zIndex: 215 }}>
            <PolylineDecorator positions={positions} pathColor={pathColor} animated={true} />
          </Pane>
        );
      } catch (e) {
        console.warn('Failed to decode overview polyline:', e);
      }
    }

    // Fallback: straight lines between stops (with starting point)
    const positions: [number, number][] = [];
    if (plannedRoute.startLocation) {
      positions.push([plannedRoute.startLocation.lat, plannedRoute.startLocation.lng]);
    }
    orderedStops.forEach(stop => {
      positions.push([stop.stop_location.latitude, stop.stop_location.longitude]);
    });

    return (
      <Pane name="planned-route" style={{ zIndex: 215 }}>
        <PolylineDecorator positions={positions} pathColor={pathColor} animated={true} />
      </Pane>
    );
  }, [isPlannedRouteResults, plannedRoute, state_TourState?.stops, state_TourState?.config]);

  // Starting point marker for planned route — disabled by request.
  // The polyline alone visualises the start now; uncomment to restore the green flag marker.
  const renderStartingPointMarker = null;


  const getGeolocation = useCallback(() => getOnDemand().geolocation, [getOnDemand]);

  return (
    <>
      {/* Map + markers + clusters + routes + drop pin controls */}
      {state_TourState?.stops.length && (
        <div
          ref={mapContainerDivRef}
          data-map-container
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: '100dvh',
          }}
        >
          <MapContainer
            ref={mapRef}
            style={{ width: '100%', height: '100%' }}
            center={position}
            zoom={currentZoom}
            zoomControl={false}
            {...(() => {
              // Tighter zoom granularity when the visible map area is narrow.
              // Matches the sidebar staging at line 807: only ≥1400px viewports
              // give the map enough horizontal room that integer zoom looks fine.
              const w = typeof window !== 'undefined' ? window.innerWidth : 0;
              const snap = w >= 1400 ? 1 : 0.5;
              return { zoomSnap: snap, zoomDelta: snap };
            })()}
            // leaflet-rotate options — keep programmatic rotation (compass/heading-up button uses setBearing),
            // but disable the two-finger gesture so users can't rotate by hand. Built-in compass hidden (we render our own).
            {...({ rotate: true, touchRotate: false, rotateControl: false, bearing: 0 } as any)}
          >
            <MapZoomHandler
              onZoomChange={setCurrentZoom}
              onUpdateClusters={updateClusters}
              filteredStops={state_TourState?.stops}
              stopId={state_activeStopState?.id}
              onMapReady={() => {
                setIsMapReady(true);
                renderStore.setAppPhase('interaction');
                if (mapRef.current) {
                  setMapRefAtom(mapRef.current);
                }
              }}
              initialFocusStop={state_activeStopState}
              savedStopState={getOnDemand().savedStopState}
              hasSetInitialView={hasSetInitialViewRef}
              tourConfigFocus={typeof state_TourState?.config?.focus_stop !== 'undefined' ? Number((state_TourState!.config as any).focus_stop) : undefined}
              initialFlyDuration={initialFlyDuration}
              doneFirstZoomRef={doneFirstZoomRef}
              getGeolocation={() => getOnDemand().geolocation}
              onUserDrag={() => { isTrackingRef.current = false; locationBtnStateRef.current = 'stops'; }}
            />
            <MapFilterFocusHandler
              stops={visibleStops}
              flyToFitStops={flyToFitStops}
              doneFirstZoomRef={doneFirstZoomRef}
              selectedFilters={selectedFilter}
              originalTour={state_TourState}
            />
            <FitBoundsHandler mapRef={mapRef} />
            <MapBearingCSSSync />
            <DeviceLocationV2 mapRef={mapRef} isTrackingRef={isTrackingRef} />
            {state_routeToStopLocation && (
              <NavigationModeGate>{renderTransitMarkers}</NavigationModeGate>
            )}
            <OfflineTileLayer />
            <TourBoundaries
              color={state_TourState?.config?.mainColor || '#2E7D32'}
              fillOpacity={0.15}
              weight={3}
              opacity={0.8}
              shadow={true}
              shadowBlur={15}
              maskMode={true}
            />
            <MapPinLayer
              clusters={clusters}
              individualMarkers={individualMarkers}
              clusteredUserPlaceIds={clusteredUserPlaceIds}
              animatedMarkerIds={animatedMarkerIds}
              animatedClusterIds={animatedClusterIds}
              currentZoom={currentZoom}
              activeStopId={state_activeStopState?.id}
              isDemo={isDemo}
              showClustersAndMarkers={state_showClustersAndMarkers}
              activeStop={state_activeStopState}
              userAddedPlaces={userAddedPlaces[state_TourState?.id || ''] || []}
              tourId={state_TourState?.id || ''}
              tourConfig={state_TourState?.config}
              originalTourStops={state_TourState?.stops}
              currentStops={state_TourState?.stops}
              allRoutes={allRoutes}
              getPinSize={getPinSize}
              getPinDrawer={getPinDrawer}
              handleClusterClick={handleClusterClick}
              dropPinStep={dropPinMode.step}
              dropPinTempPlaceId={dropPinMode.tempPlaceId}
              isPlannedRouteResults={isPlannedRouteResults}
              plannedRouteStopOrder={plannedRoute?.stopOrder}
              isDesktop={isDesktop}
              hasRouteToStop={!!state_routeToStopLocation}
              themePrimaryColor={_g.theme.palette.primary.main}
            />
            <TourRouteGate>{renderedRoutes}</TourRouteGate>
            {renderPlannedRoutePolylines}
            {renderStartingPointMarker}
            <MapStageControls
              isDesktop={isDesktop}
              sidebarWidthPx={sidebarWidthPx}
              onZoomChange={setCurrentZoom}
              onLocationClick={() => {
                const geo = getOnDemand().geolocation;
                if (geo.latitude && geo.longitude && mapRef.current) {
                  mapRef.current.flyTo([geo.latitude, geo.longitude], 16, { duration: 1.5 });
                }
              }}
              containerRef={mapContainerDivRef}
              mapRef={mapRef}
              isTrackingRef={isTrackingRef}
              locationBtnStateRef={locationBtnStateRef}
              getGeolocation={getGeolocation}
              allStops={visibleStops}
              flyToFitStops={flyToFitStops}
            />
          </MapContainer>

          {/* Drop Pin Controls popup - CSS fixed to center */}
          <MapDropPinControls
            mapRef={mapRef}
            getPinSize={getPinSize}
            currentZoom={currentZoom}
            tourState={state_TourState}
            userAddedPlaces={userAddedPlaces}
            setUserAddedPlaces={setUserAddedPlaces}
          />
        </div>
      )}
    </>
  );
});
